import {Injectable} from "@angular/core";
import {RequesterService} from "../../../shared/services/requester/requester.service";
import * as fromStore from "../../../store";
import {Store} from "@ngrx/store";
import {DbStoreState} from "../../../store";
import {combineLatest} from "rxjs";
import {Observable} from "rxjs";
import {IBatchUpdate, IEcommerceProduct} from "./ecommerce-product.interface";
import {HttpClient} from "@angular/common/http";
import {IVariantsUpload} from "./products/add-product/add-product-form.interface";
import {Config} from "../../../config/config";
import {DbResponse} from "../../../shared/interceptors/db-response";
import mockData from "../../../../assets/json/mock-data.json";
import {BehaviorSubject} from "rxjs";
import {GoogleAnalyticsService} from "../../../shared/services/google-analytics/google-analytics.service";
import {TypeUtils} from "../../../shared/utils/type-utils";

@Injectable()
export class EcommerceService {
  private _ownerWorkspaceId: number;
  private _currentWorkspaceId: number;

  constructor(private _requester: RequesterService,
              private _httpClient: HttpClient,
              private _googleAnalyticsService: GoogleAnalyticsService,
              private _store: Store<DbStoreState>) {
    this._getWorkspaceEntities();
  }

  getProducts(domainID: number, params): Observable<any> {
    if (domainID === Config.demoDomainId) {
      const response: any = mockData['products'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return this._requester.tenWebWoocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=products`, params, true);
  }

  getAllCategories(domainID: number): Observable<any> {
    return this._requester.woocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=products/categories`, {per_page: 100}, true);
  }

  deleteProduct(domainID: number, id: number): Observable<any> {
    return this._requester.woocom
      .delete(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=products/${id}`, null, true);
  }

  duplicateProduct(domainID: number, id: number): Observable<any> {
    return this._requester.woocom
      .post(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=products/${id}/duplicate`, null, true);
  }

  updateProduct(domainID: number, id: number, data: Partial<IEcommerceProduct>): Observable<any> {
    return this._requester.tenWebWoocom
      .post(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=update_product/${id}`, null, data);
  }

  getProductById(domainID: number, id: number): Observable<any> {
    return this._requester.tenWebWoocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=products/${id}`, true, true);
  }

  getVariations(domainID: number, id: number): Observable<any> {
    return this._requester.woocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=products/${id}/variations`, {per_page: 100}, true);
  }

  createMedia(domainID: number, data: any): Observable<any> {
    return this._requester.tenWebWoocom
      .post(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=create_media_bulk`, null, data, true, false, true);
  }

  batchCreateAttributes(domainID: string, data: any): Observable<any> {
    return this._requester.woocom
      .post(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=products/attributes/batch`, null, data);
  }

  createProduct(domainID: string, data: Partial<IEcommerceProduct>): Observable<any> {
    return this._requester.tenWebWoocom
      .post(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=products`, null, data);
  }

  createCategory(domainID: string, data: { name: string, parent?: number }): Observable<any> {
    return this._requester.woocom
      .post(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=products/categories`, null, data);
  }

  createTag(domainID: string, data: { name: string }): Observable<any> {
    return this._requester.woocom
      .post(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=products/tags`, null, data);
  }

  batchUpdateVariations(domainID: string, productId: string, data: IBatchUpdate<IVariantsUpload[]>) {
    return this._requester.woocom
      .post(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=products/${productId}/variations/batch`, null, data);
  }

  updatePaymentListing(domainID: number, orderingData) {
    let data = {
      "gateways": JSON.stringify(orderingData)
    };

    return this._requester.tenWebWoocom
      .post(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=payment_methods_positions`, null, data);
  }

  setDefaultWeightType(domainID: string, data: { value: string }) {
    return this._requester.woocom
      .post(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=settings/products/woocommerce_weight_unit`, null, data);
  }

  getPaymentMethods(domainID: number) {
    return this._requester.woocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=payment_gateways`, null, true);
  }

  updatePaymentMethods(domainID: number, id: string, orderNumber: number) {
    const data = {order: orderNumber};
    return this._requester.woocom
      .put(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=payment_gateways/${id}`, null, data);
  }

  private _getWorkspaceEntities(): void {
    combineLatest(
      this._store.select(fromStore.getUserSubscription),
      this._store.select(fromStore.getWorkspaceIdEntities)
    ).subscribe(([userSubscription, workspaceIdEntities]) => {
      this._ownerWorkspaceId = workspaceIdEntities.ownerWorkspaceId;
      this._currentWorkspaceId = workspaceIdEntities.currentWorkspaceId || this._ownerWorkspaceId;
    });
  }

  getProductAutocompleteLists(domainID: number) {
    return this._requester.tenWebWoocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=edit_product`, null, true);
  }

  payEngineSettings(domainID: number): Observable<any> {
    return this._requester.tenWebWoocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}/?endpoint=settings`, null, true);
  }


  public sendAnalyticsEvent(action, label, error = false) {
    const analyticsData = {
      eventCategory: error ? 'Dashboard Error' : 'Dashboard Action',
      eventAction: action,
      eventLabel: label
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  public getEcommerceSetupListData(domainID: number): Observable<any> {
    if (+domainID === Config.demoDomainId) {
      const response: any = mockData['setup-guide'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return this._requester.tenWebWoocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}/?endpoint=set_up_info`, null, true);
  }


  public switchTestMode(domainID: number, mode: string): Observable<any> {
    const data = {
      mode: mode
    };
    return this._requester.tenWebWoocom
      .post(`workspaces/${this._currentWorkspaceId}/domains/${domainID}/?endpoint=payment_test_mode`, null, data, true);
  }

  public setDefaultCountry(domainID: number, data: { default_country: string }): Observable<any> {
    return this._requester.tenWebWoocom
      .post(`workspaces/${this._currentWorkspaceId}/domains/${domainID}/?endpoint=set_default_country`, null, data, true);
  }

  public getMediaList(domainID: number) {
    return this._requester.tenWebWoocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}/?endpoint=media`, {per_page: 100});
  }

  public removeMediaItem(domainID: number, id: number) {
    return this._requester.tenWebWoocom
      .delete(`workspaces/${this._currentWorkspaceId}/domains/${domainID}/?endpoint=media/${id}&force=1`, {per_page: 100});
  }

  public createRefund(domainID: number, orderId: number, data: any, shippingData: any): Observable<any> {
    if (Object.keys(shippingData).length !== 0) {
      data.line_items.push(shippingData);
    }
    return this._requester.woocom
      .post(`workspaces/${this._currentWorkspaceId}/domains/${domainID}/?endpoint=orders/${orderId}/refunds`, null, data);
  }

  public getOrderRefunds(domainID: number, orderId: number): Observable<any> {
    return this._requester.woocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}/?endpoint=orders/${orderId}/refunds`, null);
  }

  public getRefund(domainID: number, orderId: number, refundId: number): Observable<any> {
    return this._requester.woocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}/?endpoint=orders/${orderId}/refunds/${refundId}`
      );
  }

  public getAnalyticsData(domainID: number, datePicker): Observable<any> {
    if (+domainID === Config.demoDomainId) {
      const response: any = mockData['ecommerce-analytics'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return this._requester.woocomAnalytics
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=analytics&` + datePicker, true, true);
  }


  getEmailsList(domainID: number): Observable<any> {
    if (+domainID === Config.demoDomainId) {
      const response: any = mockData['get-emails'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return this._requester.tenWebWoocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=get_emails`, null, true);
  }

  getGeneralSettings(domainID: number): Observable<any> {
    if (+domainID === Config.demoDomainId) {
      const response: any = mockData['settings-general'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return this._requester.woocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}/?endpoint=settings/general`, null, true);
  }

  getTaxSettings(domainID: number): Observable<any> {
    if (+domainID === Config.demoDomainId) {
      const response: any = mockData['setting-taxes'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return this._requester.woocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=settings/tax`, null, true);
  }

  getTaxRates(domainID: number): Observable<any> {
    if (+domainID === Config.demoDomainId) {
      const response: any = mockData['taxes'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return this._requester.woocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=taxes`, null, true);
  }

  updateTaxRates(domainID: number, taxRateId: number, data): Observable<any> {
    return this._requester.woocom
      .put(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=taxes/${taxRateId}`, null, data);
  }

  addTaxRates(domainID: number, data): Observable<any> {
    return this._requester.woocom
      .post(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=taxes`, null, data);
  }

  getEcommerceCountriesList(domainID: number): Observable<any> {
    return this._requester.woocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=data/countries`, null, true);
  }

  getEcommerceTaxClasses(domainID: number): Observable<any> {
    if (+domainID === Config.demoDomainId) {
      const response: any = mockData['taxes-classes'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return this._requester.woocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=taxes/classes`, null, true);
  }


  updateTaxSettings(domainID: number, taxData: any): Observable<any> {
    const params = [];

    if (TypeUtils.isDefined(taxData.basedOn)) {
      params.push({
        id: "woocommerce_tax_based_on",
        value: taxData.basedOn
      });
    }

    if (TypeUtils.isDefined(taxData.includeTax)) {
      params.push({
        id: "woocommerce_prices_include_tax",
        value: taxData.includeTax
      });
      params.push({
        id: "woocommerce_tax_display_shop",
        value: taxData.includeTax === 'no' ? 'excl' : 'incl'
      });
      params.push({
        id: "woocommerce_tax_display_cart",
        value: taxData.includeTax === 'no' ? 'excl' : 'incl'
      });
    }

    if (TypeUtils.isDefined(taxData.shippingClass)) {
      params.push({
        id: "woocommerce_shipping_tax_class",
        value: taxData.shippingClass === 'standard' ? '' : taxData.shippingClass
      });
    }

    if (TypeUtils.isDefined(taxData.taxTotal)) {
      params.push({
        id: "woocommerce_tax_total_display",
        value: taxData.taxTotal
      });
    }

    if (TypeUtils.isDefined(taxData.connectTaxes)) {
      params.push({
        id: "wc_connect_taxes_enabled",
        value: taxData.connectTaxes
      });
    }

    const data = {'update': params};
    return this._requester.woocom
      .post(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=settings/tax/batch`, null, data);
  }

  enableCalcTaxes(domainID: number, enabled: boolean): Observable<any> {
    const params = [
      {
        "id": "woocommerce_calc_taxes",
        "value": !!enabled ? "yes" : "no"
      }
    ];
    const data = {'update': params};
    return this._requester.woocom
      .put(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=settings/general/batch`, null, data);
  }

  getEmailsSettings(domainID: number): Observable<any> {
    if (+domainID === Config.demoDomainId) {
      const response: any = mockData['email'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return this._requester.woocom
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=settings/email`, null, true);
  }

  updateEmailsSettings(domainID: number, params: any): Observable<any> {
    const data = {'update': params};
    return this._requester.woocom
      .put(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=settings/email/batch`, null, data);
  }

  updateEmailsStatus(domainID: number, emailName: string, enabled: boolean): Observable<any> {
    const params = [
      {
        "id": "enabled",
        "value": !!enabled ? "yes" : "no"
      }
    ];
    const data = {'update': params};

    return this._requester.woocom
      .put(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=settings/${emailName}/batch`, null, data);
  }

  public flushCache(domainID: number): Observable<any> {
    return this._requester.woocomAnalytics
      .delete(`workspaces/${this._currentWorkspaceId}/domains/${domainID}/cache/flush`
      );
  }

  public getTotalStats(domainID: number, datePicker, forceCharge: boolean = false): Observable<any> {
    const timeLine = datePicker + '&stats=revenue/total_sales,revenue/net_revenue,orders/orders_count,products/items_sold,taxes/total_tax,variations/items_sold' + (forceCharge ? '&force=1' : '');
    return this._requester.woocomAnalytics
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=performance-indicators` + timeLine,
        true, true);
  }

  public getPerformanceReportOrders(domainID: number, datePicker, forceCharge: boolean = false): Observable<any> {
    const timeLine = datePicker + '&fields=orders_count&fields=avg_order_value';
    return this._requester.woocomAnalytics
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=orders/stats&order=asc&interval=day&per_page=100&` + timeLine + (forceCharge ? '&force=1' : ''),
        true, true);
  }

  public getPerformanceReportNetSales(domainID: number, datePicker, forceCharge: boolean = false): Observable<any> {
    const timeLine = datePicker + '&fields=orders_count&fields=avg_order_value';
    return this._requester.woocomAnalytics
      .get(`workspaces/${this._currentWorkspaceId}/domains/${domainID}?endpoint=revenue/stats&order=asc&interval=day&per_page=100&` + timeLine + (forceCharge ? '&force=1' : ''),
        true, true);
  }
}
